import { useEffect, useState } from 'react'
import api from '../services/api'

// Faixa nas telas de emissão: última nota emitida (autorizada/cancelada) no modelo/série e o número desta.
// Número pulado (não existe em situação nenhuma) trava a emissão no backend; aqui aparece vermelho com o
// botão "Usar o Nº X", que volta o contador pro primeiro número pulado e devolve o número pra tela.
interface Props {
  empcod?: number | null; modelo: string; serie?: string | number | null; proximo?: string | number | null
  onNumeroAjustado?: (numero: number) => void
  nacional?: boolean // NFS-e Nacional: Sefin numera a nota; "proximo" é a DPS, sem trava de pulo
}
interface Situacao {
  ultima: { numero: number; serie: string; data: string | null; situacao: string } | null
  faltando: number[]
}

const dataBr = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '')

export default function AvisoUltimaNota({ empcod, modelo, serie, proximo, onNumeroAjustado, nacional }: Props) {
  const [sit, setSit] = useState<Situacao | null>(null)
  const [ajustando, setAjustando] = useState(false)
  const [erro, setErro] = useState('')
  const prox = Number(proximo) || 0

  useEffect(() => {
    setErro('')
    if (!empcod || serie == null || serie === '' || !prox) { setSit(null); return }
    api.get('/erp/ultima-emitida', { params: { empcod, modelo, serie, proximo: prox } })
      .then(r => setSit(r.data)).catch(() => setSit(null))
  }, [empcod, modelo, serie, prox])

  async function usarNumero(numero: number) {
    setAjustando(true); setErro('')
    try {
      await api.post('/erp/numeracao/usar', { empcod, modelo, serie, proximoAtual: prox, numero })
      onNumeroAjustado?.(numero)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao ajustar a numeração.')
    } finally {
      setAjustando(false)
    }
  }

  if (!sit || !prox) return null
  const { ultima, faltando } = sit

  if (nacional) {
    return (
      <div style={{
        background: '#eff6ff', border: '1px solid #1b3f7b', borderLeft: '5px solid #1b3f7b', borderRadius: 6,
        padding: '0.6rem 0.9rem', margin: '0 0 0.9rem', color: '#1e3a8a', fontSize: '0.85rem', lineHeight: 1.45,
      }}>
        {ultima
          ? <>Última NFS-e emitida: <strong>Nº {ultima.numero}</strong>{ultima.data ? ` em ${dataBr(ultima.data)}` : ''}{ultima.situacao === 'CANCELADA' ? ' — cancelada' : ''}. Esta deve ser a <strong>Nº {ultima.numero + 1}</strong> (número dado pela Sefin).</>
          : <>Nenhuma NFS-e emitida ainda. O número da nota é dado pela Sefin.</>}
        <div style={{ fontSize: '0.78rem', marginTop: 2 }}>Nº interno da DPS: {prox}</div>
      </div>
    )
  }
  const travado = faltando.length > 0
  const menor = ultima != null && prox <= ultima.numero
  const cor = travado || menor ? { bg: '#fef2f2', borda: '#dc2626', txt: '#991b1b' } : { bg: '#eff6ff', borda: '#1b3f7b', txt: '#1e3a8a' }

  return (
    <div style={{
      background: cor.bg, border: `1px solid ${cor.borda}`, borderLeft: `5px solid ${cor.borda}`, borderRadius: 6,
      padding: '0.6rem 0.9rem', margin: '0 0 0.9rem', color: cor.txt, fontSize: '0.85rem', lineHeight: 1.45,
    }}>
      {ultima ? (
        <div>
          Última nota emitida: <strong>Nº {ultima.numero}</strong> (série {ultima.serie}){ultima.data ? ` em ${dataBr(ultima.data)}` : ''}
          {ultima.situacao === 'CANCELADA' ? ' — cancelada' : ''}. Esta nota será a <strong>Nº {prox}</strong>.
        </div>
      ) : (
        <div>Nenhuma nota emitida ainda nesta série. Esta nota será a <strong>Nº {prox}</strong>.</div>
      )}
      {travado && (
        <div style={{ marginTop: 4 }}>
          <div style={{ fontWeight: 700 }}>
            ⛔ Emissão bloqueada: {faltando.length === 1
              ? `o número ${faltando[0]} não existe`
              : `os números ${faltando[0]} a ${faltando[faltando.length - 1]} não existem`} (nem autorizados, nem pendentes).
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
            <button type="button" disabled={ajustando} onClick={() => usarNumero(faltando[0])}
              style={{ padding: '0.35rem 0.8rem', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
              {ajustando ? 'Ajustando...' : `Usar o Nº ${faltando[0]}`}
            </button>
            <span style={{ fontSize: '0.78rem' }}>ou corrija o próximo número na TPARVEN.</span>
          </div>
        </div>
      )}
      {menor && <div style={{ fontWeight: 700, marginTop: 3 }}>⚠ Atenção: o número desta nota é menor ou igual ao último emitido.</div>}
      {erro && <div style={{ marginTop: 4, fontWeight: 600 }}>{erro}</div>}
    </div>
  )
}
